import { useEffect, useId, useRef, useState } from "react";
import { MdMoreVert } from "react-icons/md";
import { IconButton } from "components/button";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * ActionMenu: an IconButton that opens a small menu of row actions.
 *
 * @param {object} props
 * @param {string} props.label Accessible name for the trigger (e.g. `Actions for "Welcome"`).
 * @param {Array<{id: string, label: string, icon?: React.ComponentType, onSelect?: () => void,
 *   tone?: "default"|"danger", disabled?: boolean}>} props.items
 * @param {React.ComponentType} [props.icon=MdMoreVert]
 * @param {"secondary"|"ghost"} [props.variant="secondary"]
 * @param {"sm"|"md"} [props.size="md"]
 */
const ActionMenu = ({ label, items, icon: Icon = MdMoreVert, variant = "secondary", size = "md" }) => {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);

  const enabledIndexes = items
    .map((item, index) => (item.disabled ? null : index))
    .filter((index) => index !== null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [open]);

  useEffect(() => {
    if (open && enabledIndexes.length) itemRefs.current[enabledIndexes[0]]?.focus();
    // Focus the first item only when the menu opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const moveFocus = (currentIndex, step) => {
    if (!enabledIndexes.length) return;
    const position = enabledIndexes.indexOf(currentIndex);
    const next = enabledIndexes[(position + step + enabledIndexes.length) % enabledIndexes.length];
    itemRefs.current[next]?.focus();
  };

  const handleMenuKeyDown = (event, index) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(index, 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(index, -1);
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <IconButton
        ref={triggerRef}
        icon={<Icon />}
        label={label}
        variant={variant}
        size={size}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      />
      {open && (
        <ul
          id={menuId}
          role="menu"
          aria-label={label}
          className="absolute right-0 top-full z-20 mt-1 w-56 rounded-control border border-line bg-surface py-1 shadow-overlay"
        >
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <li key={item.id} role="none">
                <button
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  tabIndex={-1}
                  onKeyDown={(event) => handleMenuKeyDown(event, index)}
                  onClick={() => {
                    close();
                    item.onSelect?.();
                  }}
                  className={cx(
                    "flex w-full items-center gap-2 px-4 py-2 text-left text-sm focus:bg-surface-subtle focus:outline-none disabled:cursor-not-allowed disabled:text-fg-muted",
                    item.tone === "danger" ? "text-danger-fg" : "text-fg",
                    !item.disabled && "hover:bg-surface-subtle"
                  )}
                >
                  {ItemIcon && <ItemIcon aria-hidden="true" className="h-4 w-4 shrink-0" />}
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ActionMenu;
